import React from "react";
import { Typography, Divider } from "antd";
import AccordionSection from "../../components/AccordionComponent";

const { Title, Paragraph, Text } = Typography;

export default function BankInterestTypeHelpIndex() {
  return (
    <div
      className="help__page max-w-5xl mx-auto"
      style={{ fontFamily: "Arial, sans-serif", padding: 24 }}
    >
      <Title level={3} style={{ textAlign: "center" }}>
        Bank Interest Schemes - Help Guide
      </Title>

      <Divider />

      {/* Overview */}
      <AccordionSection title="Overview">
        <Paragraph>
          Our system supports flexible interest calculation methods to accommodate
          different banking practices. Each interest scheme can be configured
          with multiple rate slabs based on the loan duration, and you can choose
          from five different calculation methods.
        </Paragraph>
      </AccordionSection>

      {/* Creating Interest Schemes */}
      <AccordionSection title="Creating Interest Schemes">
        <Paragraph strong>Required Information</Paragraph>

        <Paragraph>
          When creating a new bank interest scheme, you will need to configure:
        </Paragraph>

        <Paragraph>
          <Text strong>1. Basic Details</Text>
          <br />- Bank: Select the bank for this scheme
          <br />- Branch: Choose the specific branch
          <br />- Total Months: Maximum loan duration (tenure)
          <br />- Scheme Limit: Maximum loan amount allowed
          <br />- Interest Type: Type of interest calculation (used for internal categorization)
        </Paragraph>

        <Paragraph>
          <Text strong>2. Interest Rate Slabs</Text>
          <br />- Start Date: Beginning day number for this rate (e.g., Day 1, Day 31)
          <br />- End Date: Ending day number for this rate (e.g., Day 30, Day 90)
          <br />- Interest Rate: Annual interest percentage for this slab
        </Paragraph>

        <Paragraph>
          <Text strong>3. Additional Charges</Text>
          <br />- Document Charge Type & Amount: One-time processing fee
          <br />- Appraisal Charge Type & Amount: Gold appraisal fee
          <br />- Minimum Gold Rate: Minimum rate per gram for valuation
        </Paragraph>

        <Paragraph>
          <Text strong>4. Calculation Method</Text>
          <br />- Choose from: Simple, Compound, Loan, Fixed, or Flat
        </Paragraph>
      </AccordionSection>

      {/* Interest Calculation Types */}
      <AccordionSection title="Interest Calculation Types">
        {/* 1. Simple Interest */}
        <Paragraph strong>1. Simple Interest</Paragraph>

        <Paragraph>
          <Text strong>Best for:</Text> Traditional pledge schemes with progressive rates
        </Paragraph>

        <Paragraph strong>How it works:</Paragraph>
        <Paragraph>
          - Interest increases proportionally with time
          <br />- Different rates apply to different periods
          <br />- Formula: (Days × Principal × Interest%) / 365
        </Paragraph>

        <Paragraph strong>Calculation Method:</Paragraph>
        <Paragraph>
          - The system divides the loan period into slabs
          <br />- Each slab&apos;s days are multiplied by its specific interest rate
          <br />- All slab interests are added together
        </Paragraph>

        <Paragraph strong>Example:</Paragraph>
        <Paragraph>
          - Principal: ₹10,000
          <br />- Period: 45 days
          <br />- Rates: Days 1-30 @ 12%, Days 31-60 @ 15%
        </Paragraph>

        <Paragraph>
          Days 1-30: (30 × 10,000 × 12%) / 365 = ₹98.63
          <br />
          Days 31-45: (15 × 10,000 × 15%) / 365 = ₹61.64
          <br />
          Total Interest: ₹160.27
        </Paragraph>

        <Divider />

        {/* 2. Compound Interest */}
        <Paragraph strong>2. Compound Interest</Paragraph>

        <Paragraph>
          <Text strong>Best for:</Text> Long-term pledges where interest compounds monthly
        </Paragraph>

        <Paragraph strong>How it works:</Paragraph>
        <Paragraph>
          - Interest is calculated monthly
          <br />- At the end of each month, calculated interest is added to principal
          <br />- Next month&apos;s interest is calculated on the new principal (principal + interest)
          <br />- Formula: (Days × Current Principal × Interest%) / 365
        </Paragraph>

        <Paragraph strong>Calculation Method:</Paragraph>
        <Paragraph>
          - Interest is calculated for each calendar month separately
          <br />- On the last day of each month, interest is added to principal
          <br />- The compounded principal is used for the next month
        </Paragraph>

        <Paragraph strong>Example:</Paragraph>
        <Paragraph>
          - Principal: ₹10,000
          <br />- Period: 65 days (crossing 2 month-ends)
          <br />- Rate: 18%
        </Paragraph>

        <Paragraph>
          Month 1 (30 days): (30 × 10,000 × 18%) / 365 = ₹147.95
          <br />
          New Principal: ₹10,147.95
          <br /><br />
          Month 2 (30 days): (30 × 10,147.95 × 18%) / 365 = ₹150.14
          <br />
          New Principal: ₹10,298.09
          <br /><br />
          Month 3 (5 days): (5 × 10,298.09 × 18%) / 365 = ₹25.41
          <br />
          Total Interest: ₹323.50
        </Paragraph>

        <Divider />

        {/* 3. Loan Interest */}
        <Paragraph strong>3. Loan Interest</Paragraph>

        <Paragraph>
          <Text strong>Best for:</Text> Personal loans where one rate applies to the entire duration
        </Paragraph>

        <Paragraph strong>How it works:</Paragraph>
        <Paragraph>
          - The system finds which slab the total loan duration falls into
          <br />- That slab&apos;s interest rate is applied to all days (not progressive)
          <br />- Formula: (Total Days × Principal × Applicable Slab Interest%) / 365
        </Paragraph>

        <Paragraph strong>Calculation Method:</Paragraph>
        <Paragraph>
          - Total days are calculated
          <br />- The slab containing these days determines the rate
          <br />- Interest is calculated on all days at this single rate
        </Paragraph>

        <Paragraph strong>Example:</Paragraph>
        <Paragraph>
          - Principal: ₹10,000
          <br />- Period: 45 days
          <br />- Slabs: Days 1-30 @ 12%, Days 31-90 @ 15%
        </Paragraph>

        <Paragraph>
          45 days falls in the 31-90 slab, so 15% applies to ALL 45 days:
          <br />
          Interest: (45 × 10,000 × 15%) / 365 = ₹184.93
        </Paragraph>

        <Paragraph>
          <Text strong>Note:</Text> Unlike Simple Interest, the rate for days 1-30 is NOT 12%.
          All 45 days use 15%.
        </Paragraph>

        <Divider />

        {/* 4. Fixed Interest */}
        <Paragraph strong>4. Fixed Interest</Paragraph>

        <Paragraph>
          <Text strong>Best for:</Text> Schemes with a flat monthly charge regardless of principal changes
        </Paragraph>

        <Paragraph strong>How it works:</Paragraph>
        <Paragraph>
          - A fixed amount of interest is charged per month
          <br />- Formula: Principal × (Interest% / 100) per complete month
          <br />- For partial months: (Remaining Days / Days in Month) × Monthly Interest
        </Paragraph>

        <Paragraph strong>Calculation Method:</Paragraph>
        <Paragraph>
          - Calculate monthly interest: Principal × (Rate / 100)
          <br />- Count complete months and charge full monthly interest for each
          <br />- For the partial month, charge proportionally based on days
        </Paragraph>

        <Paragraph strong>Example:</Paragraph>
        <Paragraph>
          - Principal: ₹10,000
          <br />- Period: 65 days (2 complete months + 5 days)
          <br />- Rate: 18%
        </Paragraph>

        <Paragraph>
          Monthly Interest: 10,000 × (18 / 100) = ₹1,800
          <br />
          Month 1: ₹1,800
          <br />
          Month 2: ₹1,800
          <br />
          Month 3 (5 days): (5 / 30) × ₹1,800 = ₹300
          <br />
          Total Interest: ₹3,900
        </Paragraph>

        <Paragraph>
          <Text strong>Note:</Text> This method charges significantly higher interest but is simple to understand.
        </Paragraph>

        <Divider />

        {/* 5. Flat Interest */}
        <Paragraph strong>5. Flat Interest</Paragraph>

        <Paragraph>
          <Text strong>Best for:</Text> Hybrid schemes that switch from simple to loan-based after a threshold
        </Paragraph>

        <Paragraph strong>How it works:</Paragraph>
        <Paragraph>
          - Initially calculates as Simple Interest
          <br />- After crossing a threshold (second-to-last slab), switches to Loan Interest method
          <br />- The widest slab&apos;s rate is applied to all days
        </Paragraph>

        <Paragraph strong>Calculation Method:</Paragraph>
        <Paragraph>
          - If days ≤ second-to-last slab&apos;s end date: Use Simple Interest
          <br />- If days &gt; second-to-last slab&apos;s end date: Use Loan Interest with the widest slab&apos;s rate
        </Paragraph>

        <Paragraph strong>Example:</Paragraph>
        <Paragraph>
          - Principal: ₹10,000
          <br />- Slabs:
          <br />&nbsp;&nbsp;• Days 1-30 @ 12%
          <br />&nbsp;&nbsp;• Days 31-90 @ 15%
          <br />&nbsp;&nbsp;• Days 1-180 @ 18% (widest slab)
        </Paragraph>

        <Paragraph>
          <Text strong>Scenario A:</Text> 45 days
          <br />
          45 ≤ 90, so Simple Interest applies
          <br />
          Total: ₹160.27
        </Paragraph>

        <Paragraph>
          <Text strong>Scenario B:</Text> 120 days
          <br />
          120 &gt; 90, so Loan Interest with widest slab (18%) applies
          <br />
          Interest: (120 × 10,000 × 18%) / 365 = ₹591.78
        </Paragraph>
      </AccordionSection>

      {/* Understanding Interest Rate Slabs */}
      <AccordionSection title="Understanding Interest Rate Slabs">
        <Paragraph strong>What are Slabs?</Paragraph>

        <Paragraph>
          Slabs define different interest rates for different periods of the loan.
          Think of them as tiers or brackets.
        </Paragraph>

        <Paragraph strong>How to Configure Slabs</Paragraph>

        <Paragraph strong>Progressive Slabs (for Simple/Flat):</Paragraph>
        <Paragraph>
          Slab 1: Days 1-30 @ 12%
          <br />
          Slab 2: Days 31-90 @ 15%
          <br />
          Slab 3: Days 91-180 @ 18%
        </Paragraph>

        <Paragraph strong>Single Rate Slabs (for Loan/Compound):</Paragraph>
        <Paragraph>
          Slab 1: Days 1-90 @ 15%
          <br />
          Slab 2: Days 91-180 @ 18%
          <br />
          Slab 3: Days 181-365 @ 20%
        </Paragraph>

        <Paragraph strong>Widest Slab (for Flat):</Paragraph>
        <Paragraph>
          Slab 1: Days 1-30 @ 12%
          <br />
          Slab 2: Days 31-90 @ 15%
          <br />
          Slab 3: Days 1-180 @ 18% ← Widest slab (covers most days)
        </Paragraph>

        <Paragraph strong>Best Practices</Paragraph>

        <Paragraph>
          1. Always include a slab starting from Day 1
          <br />
          2. Ensure slabs cover your maximum tenure
          <br />
          3. For Flat Interest, create one &quot;widest&quot; slab covering the entire range
          <br />
          4. Higher rates typically apply to longer durations to compensate for liquidity risk
        </Paragraph>
      </AccordionSection>


      {/* Document Charge Handling */}
      <AccordionSection title="Document Charge Handling">
        <Paragraph>
          Document charges (processing fees) can be handled in three ways:
        </Paragraph>

        <Paragraph strong>1. Add to Principal</Paragraph>
        <Paragraph>
          - Document charge is added to the principal amount
          <br />
          - Interest is calculated on: Principal + Document Charge
          <br />
          - Use when: You want to charge interest on the processing fee
        </Paragraph>

        <Paragraph strong>Example:</Paragraph>
        <Paragraph>
          - Principal: ₹10,000
          <br />
          - Document Charge: ₹500
          <br />
          - Effective Principal for Interest: ₹10,500
        </Paragraph>

        <Paragraph strong>2. Collect on Close</Paragraph>
        <Paragraph>
          - Document charge is collected when the pledge is closed
          <br />
          - Interest is calculated only on the original principal
          <br />
          - Use when: You want to keep fees separate from the loan amount
        </Paragraph>

        <Paragraph strong>Example:</Paragraph>
        <Paragraph>
          - Principal: ₹10,000 (interest calculated on this)
          <br />
          - Document Charge: ₹500 (collected at closing)
          <br />
          - Total to Pay: Principal + Interest + ₹500
        </Paragraph>

        <Paragraph strong>3. Don&apos;t Add (Default)</Paragraph>
        <Paragraph>
          - Document charge was already deducted when creating the pledge
          <br />
          - No adjustment needed during interest calculation
          <br />
          - Use when: You collect the fee upfront
        </Paragraph>

        <Paragraph strong>Example:</Paragraph>
        <Paragraph>
          - Customer receives: ₹9,500 (after ₹500 deduction)
          <br />
          - Principal for repayment: ₹10,000
          <br />
          - Interest calculated on: ₹10,000
        </Paragraph>
      </AccordionSection>

      {/* Calculation Examples */}
      <AccordionSection title="Calculation Examples">
        <Paragraph strong>Example 1: Simple Interest with Partial Payment</Paragraph>

        <Paragraph strong>Pledge Details:</Paragraph>
        <Paragraph>
          - Principal: ₹50,000
          <br />
          - Start Date: Jan 1, 2024
          <br />
          - Calculation Type: Simple Interest
          <br />
          - Rates: Days 1-30 @ 12%, Days 31-90 @ 15%
        </Paragraph>

        <Paragraph strong>Transaction History:</Paragraph>
        <Paragraph>
          - Jan 15 (Day 15): Paid ₹2,000 (Partial Payment - Interest Only)
          <br />
          - Feb 10 (Day 41): Full Closure
        </Paragraph>

        <Paragraph strong>Calculation:</Paragraph>

        <Paragraph strong>First Period (Days 1-15):</Paragraph>
        <Paragraph>
          Days 1-15: (15 × 50,000 × 12%) / 365 = ₹246.58
          <br />
          Paid: ₹2,000
          <br />
          Remaining to deduct from next calculation: ₹1,753.42
        </Paragraph>

        <Paragraph strong>Second Period (Days 16-41):</Paragraph>
        <Paragraph>
          Days 16-30: (15 × 50,000 × 12%) / 365 = ₹246.58
          <br />
          Days 31-41: (11 × 50,000 × 15%) / 365 = ₹225.68
          <br />
          Total Interest: ₹472.26
          <br />
          Less Previous Payment Balance: ₹1,753.42
        </Paragraph>

        <Paragraph>
          Net Interest Due: -₹1,281.16 (No additional interest, customer gets ₹1,281.16 credit!)
        </Paragraph>

        <Divider />

        <Paragraph strong>Example 2: Compound Interest - Long Term</Paragraph>

        <Paragraph strong>Pledge Details:</Paragraph>
        <Paragraph>
          - Principal: ₹1,00,000
          <br />
          - Start Date: Jan 1, 2024
          <br />
          - Close Date: Apr 15, 2024 (105 days)
          <br />
          - Rate: 18% (uniform)
        </Paragraph>

        <Paragraph strong>Calculation:</Paragraph>

        <Paragraph>
          January (31 days):
          <br />
          Interest: (31 × 1,00,000 × 18%) / 365 = ₹1,528.77
          <br />
          New Principal: ₹1,01,528.77
        </Paragraph>

        <Paragraph>
          February (29 days in 2024):
          <br />
          Interest: (29 × 1,01,528.77 × 18%) / 365 = ₹1,446.58
          <br />
          New Principal: ₹1,02,975.35
        </Paragraph>

        <Paragraph>
          March (31 days):
          <br />
          Interest: (31 × 1,02,975.35 × 18%) / 365 = ₹1,574.47
          <br />
          New Principal: ₹1,04,549.82
        </Paragraph>

        <Paragraph>
          April (14 days):
          <br />
          Interest: (14 × 1,04,549.82 × 18%) / 365 = ₹714.96
          <br />
          Total Interest: ₹5,264.78
        </Paragraph>

        <Divider />

        <Paragraph strong>Example 3: Loan Interest vs Simple Interest</Paragraph>

        <Paragraph>Same scenario, different calculation types:</Paragraph>

        <Paragraph strong>Details:</Paragraph>
        <Paragraph>
          - Principal: ₹25,000
          <br />
          - Period: 50 days
          <br />
          - Slabs: Days 1-30 @ 10%, Days 31-90 @ 14%
        </Paragraph>

        <Paragraph strong>Simple Interest:</Paragraph>
        <Paragraph>
          Days 1-30: (30 × 25,000 × 10%) / 365 = ₹205.48
          <br />
          Days 31-50: (20 × 25,000 × 14%) / 365 = ₹191.78
          <br />
          Total: ₹397.26
        </Paragraph>

        <Paragraph strong>Loan Interest:</Paragraph>
        <Paragraph>
          50 days falls in 31-90 slab, so 14% applies to ALL days:
          <br />
          Interest: (50 × 25,000 × 14%) / 365 = ₹479.45
        </Paragraph>

        <Paragraph>
          Difference: Loan Interest charges ₹82.19 more because the higher rate applies to all days.
        </Paragraph>
      </AccordionSection>

      {/* Common Questions */}
      <AccordionSection title="Common Questions">
        <Paragraph strong>
          Q: Which calculation type should I use?
        </Paragraph>
        <Paragraph>
          - Simple: Most common, fair progressive rates
          <br />
          - Compound: For long-term pledges with monthly compounding
          <br />
          - Loan: For personal loans with flat rates
          <br />
          - Fixed: When you want predictable monthly charges
          <br />
          - Flat: For hybrid schemes that penalize longer durations
        </Paragraph>

        <Paragraph strong>
          Q: How are partial payments handled?
        </Paragraph>
        <Paragraph>
          - Interest-only payments reduce the interest due in the next calculation
          <br />
          - Principal payments reduce both the principal balance and future interest
        </Paragraph>

        <Paragraph strong>
          Q: What happens if I have overlapping slabs?
        </Paragraph>
        <Paragraph>
          - For Simple/Compound: The specific rate for each day range is used
          <br />
          - For Loan/Flat: The widest slab (covering most days) is typically used for threshold calculations
        </Paragraph>

        <Paragraph strong>
          Q: How is the number of days calculated?
        </Paragraph>
        <Paragraph>
          - Days are counted from the pledge start date to the current date
          <br />
          - Uses Indian timezone for day boundaries
          <br />
          - Leap years are accounted for (365 or 366 days in calculations)
        </Paragraph>
      </AccordionSection>
    </div>
  );
}